import ResponsiveImage from "./common/ResponsiveImage";
import { tr, usePartnerTranslation } from "../lib/language";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import { SUPPLIER_LOGO } from "../lib/constants";
import { defaultPageFor, useAuth } from "../lib/auth";
import { useLanguage } from "../lib/language";

const COLUMNS = [
  {
    id: "company",
    get title() { return tr("Company"); },
    links: [
      { get label() { return tr("voocly.com"); }, href: "https://www.voocly.com/" },
      { get label() { return tr("Policies"); }, to: "/partner-legal-center" },
    ],
  },
  {
    id: "support",
    get title() { return tr("Support"); },
    links: [
      { get label() { return tr("Support"); }, to: "/contact" },
      { get label() { return tr("FAQs"); }, to: "/faqs" },
    ],
  },
];

const TikTokIcon = props => {
  usePartnerTranslation();
  return <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.01a8.16 8.16 0 0 0 4.77 1.52V7.09a4.85 4.85 0 0 1-1.84-.4z" />
  </svg>;
};

const SOCIALS = [
  { Icon: Linkedin, href: "https://www.linkedin.com/company/voocly", get label() { return tr("LinkedIn"); } },
  { Icon: Instagram, href: "https://www.instagram.com/voocly", get label() { return tr("Instagram"); } },
  { Icon: Facebook, href: "https://www.facebook.com/voocly", get label() { return tr("Facebook"); } },
  { Icon: TikTokIcon, href: "https://www.tiktok.com/@.voocly", get label() { return tr("TikTok"); } },
  { Icon: Youtube, href: "https://www.youtube.com/@voocly", get label() { return tr("YouTube"); } },
];

export const Footer = () => {
  usePartnerTranslation();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const { t } = useLanguage();
  const logoTarget = user ? defaultPageFor(role) : "/";

  return (
    <footer data-testid="site-footer" className="w-full pb-6 pt-10 md:pb-10">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="humrahe-footer-bg rounded-2xl p-8 md:p-12">
          <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between">
            <div>
              <button
                data-testid="footer-logo-btn"
                onClick={() => navigate(logoTarget)}
                className="inline-flex items-center transition-opacity hover:opacity-80"
                aria-label={user ? tr("Back to dashboard") : tr("Back to login")}
              >
                <ResponsiveImage imageWidth={960}
                  src={SUPPLIER_LOGO}
                  alt="Voocly Partner"
                  className="h-16 w-auto md:h-20"
                />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-8 md:min-w-[300px] md:justify-items-start">
              {COLUMNS.map((col) => (
                <div
                  key={col.id}
                  data-testid={`footer-col-${col.id}`}
                >
                  <h4 className="mb-4 text-sm font-semibold text-[#0F1115]">
                    {t(`footer.${col.id}`, col.title)}
                  </h4>
                  <ul className="space-y-3">
                    {col.links.map((l) => (
                      <li key={l.label}>
                        {l.href ? (
                          <a
                            data-testid={"footer-link-voocly-com"}
                            href={l.href}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-[#5b5f66] transition-colors hover:text-[#FF5757]"
                          >
                            {t(`footer.${l.label.replace(/\W+/g, "").toLowerCase()}`, l.label)}
                          </a>
                        ) : (
                          <Link
                            data-testid={`footer-link-${l.to.replace("/", "")}`}
                            to={l.to}
                            className="text-sm text-[#5b5f66] transition-colors hover:text-[#FF5757]"
                          >
                            {t(`footer.${l.label.replace(/\W+/g, "").toLowerCase()}`, l.label)}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 px-2 pb-4 md:flex-row md:items-center md:justify-between md:pb-2">
          <p className="text-xs text-[#6B7280]" data-testid="footer-copyright">
            {t("footer.copyright", "© 2026 Voocly. All rights reserved.")}
          </p>
          <div className="flex items-center gap-2 md:justify-end">
            {SOCIALS.map(({ Icon, href, label }) => (
              <a
                key={label}
                data-testid={`social-${label.toLowerCase()}`}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#5b5f66] transition-colors hover:text-[#FF5757]"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
